import { DatePipe, formatDate } from '@angular/common';
import {
  afterRenderEffect,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { StreamingService } from '../../services/streaming.service';
import { BreakpointObserverUtil } from '../../utils/platform/breakpoint-observer.util';
import { isValidStreamingIframeUrl, resolveStreamingVideoIframeSrc } from '../../utils/streaming-url-validation';

const MOBILE_SOLAPA_SIZE_PX = 50;
const MOBILE_SOLAPA_EDGE_MARGIN_PX = 8;
const MOBILE_SOLAPA_DRAG_THRESHOLD_PX = 10;
const MOBILE_SOLAPA_CLICK_SUPPRESS_MS = 300;

const PLATE_SLIDE_TITLES = ['5 de Oro', 'Revancha'] as const;

export interface StreamingPlateSlide {
  imageUrl: string;
  title: string;
}

@Component({
  selector: 'app-global-streaming-overlay',
  templateUrl: './global-streaming-overlay.component.html',
  styleUrl: './global-streaming-overlay.component.scss',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [DatePipe],
})
export class GlobalStreamingOverlayComponent {
  protected readonly streaming = inject(StreamingService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly breakpoint = inject(BreakpointObserverUtil);

  private readonly platesSwiper = viewChild<ElementRef<HTMLElement>>('platesSwiper');
  private platesSwiperInitialized = false;

  /** Fecha actual para el título */
  protected readonly today = new Date();

  protected readonly isPlatesMode = computed(() => this.streaming.contentMode() === 'plates');

  private readonly isIosNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

  /** User-dragged position for mobile collapsed solapa (null = CSS default). */
  protected readonly collapsedDragRight = signal<number | null>(null);
  protected readonly collapsedDragBottom = signal<number | null>(null);
  protected readonly isSolapaDragging = signal(false);

  private dragPointerId: number | null = null;
  private dragStartClientX = 0;
  private dragStartClientY = 0;
  private dragStartRight = 0;
  private dragStartBottom = 0;
  private dragMoved = false;
  private suppressExpandUntil = 0;

  constructor() {
    afterRenderEffect(() => {
      const slides = this.plateSlides();
      const el = this.platesSwiper()?.nativeElement as
        | (HTMLElement & { initialize?: () => void; swiper?: unknown })
        | undefined;
      if (slides.length < 2 || !el) {
        this.platesSwiperInitialized = false;
        return;
      }
      if (this.platesSwiperInitialized || el.swiper) {
        return;
      }
      Object.assign(el, {
        loop: true,
        spaceBetween: 0,
        slidesPerView: 1,
        autoplay: { delay: 5000 },
        pagination: { clickable: true },
        speed: 500,
      });
      el.initialize?.();
      this.platesSwiperInitialized = true;
    });
  }

  protected get headerTitle(): string {
    return `Sorteo del ${formatDate(this.today, 'dd/MM/yyyy', 'es')}`;
  }

  protected get iframeTitleLive(): string {
    return `${this.headerTitle} — EN VIVO`;
  }

  /** Video embed (direct CMS URL on web/Android; HTTPS proxy on iOS native). */
  protected readonly safeVideoUrl = computed((): SafeResourceUrl => {
    const iframeSrc = resolveStreamingVideoIframeSrc(this.streaming.url(), {
      iosNative: this.isIosNative,
    });
    if (!iframeSrc || !isValidStreamingIframeUrl(iframeSrc)) {
      return this.sanitizer.bypassSecurityTrustResourceUrl('about:blank'); // NOSONAR: HTML from trusted content source (getPage), not user input.
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(iframeSrc); // NOSONAR: HTML from trusted content source (getPage), not user input.
  });

  /** Oro + Revancha — one full plate at a time (swiper, same UX as home banners). */
  protected readonly plateSlides = computed((): StreamingPlateSlide[] => {
    if (this.streaming.contentMode() !== 'plates') {
      return [];
    }
    const urls = this.streaming.plateUrls();
    if (urls.length < 2) {
      return [];
    }
    return [urls[0], urls[1]].map((imageUrl, index) => ({
      imageUrl,
      title: PLATE_SLIDE_TITLES[index],
    }));
  });

  protected get collapsedDragRightStyle(): string | null {
    const right = this.collapsedDragRight();
    return right === null ? null : `${right}px`;
  }

  protected get collapsedDragBottomStyle(): string | null {
    const bottom = this.collapsedDragBottom();
    return bottom === null ? null : `${bottom}px`;
  }

  private isMobileCollapsed(): boolean {
    return this.streaming.isCollapsed() && !this.breakpoint.isLargeScreen();
  }

  minimize(): void {
    this.streaming.setMiniPlayer();
  }

  onCardClick(): void {
    if (Date.now() < this.suppressExpandUntil) {
      return;
    }
    if (this.streaming.isMini() || this.streaming.isCollapsed()) {
      this.streaming.expandToCenter();
    }
  }

  /**
   * X del overlay: barra minimizada (collapsed). Nunca cierre total ni mini player.
   * El backdrop sigue yendo a mini vía {@link minimize}.
   */
  closeOrCollapseToBar(event: MouseEvent): void {
    event.stopPropagation();
    this.streaming.collapse();
  }

  expandToCenter(event: MouseEvent): void {
    event.stopPropagation();
    if (Date.now() < this.suppressExpandUntil) {
      event.preventDefault();
      return;
    }
    this.streaming.expandToCenter();
  }

  onCollapsedPointerDown(event: PointerEvent): void {
    if (!this.isMobileCollapsed() || event.button !== 0) {
      return;
    }
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dragPointerId = event.pointerId;
    this.dragStartClientX = event.clientX;
    this.dragStartClientY = event.clientY;
    this.dragStartRight = this.collapsedDragRight() ?? window.innerWidth - rect.right;
    this.dragStartBottom = this.collapsedDragBottom() ?? window.innerHeight - rect.bottom;
    this.dragMoved = false;
    target.setPointerCapture(event.pointerId);
  }

  onCollapsedPointerMove(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId || !this.isMobileCollapsed()) {
      return;
    }
    const dx = event.clientX - this.dragStartClientX;
    const dy = event.clientY - this.dragStartClientY;
    if (!this.dragMoved && Math.hypot(dx, dy) < MOBILE_SOLAPA_DRAG_THRESHOLD_PX) {
      return;
    }
    this.dragMoved = true;
    this.isSolapaDragging.set(true);
    event.preventDefault();
    this.collapsedDragRight.set(this.clampRight(this.dragStartRight - dx));
    this.collapsedDragBottom.set(this.clampBottom(this.dragStartBottom - dy));
  }

  onCollapsedPointerUp(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) {
      return;
    }
    const target = event.currentTarget as HTMLElement;
    if (target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }
    this.dragPointerId = null;
    this.isSolapaDragging.set(false);
    if (this.dragMoved) {
      this.suppressExpandUntil = Date.now() + MOBILE_SOLAPA_CLICK_SUPPRESS_MS;
    }
    this.dragMoved = false;
  }

  private clampRight(right: number): number {
    const max = Math.max(
      MOBILE_SOLAPA_EDGE_MARGIN_PX,
      window.innerWidth - MOBILE_SOLAPA_SIZE_PX - MOBILE_SOLAPA_EDGE_MARGIN_PX
    );
    return Math.min(max, Math.max(MOBILE_SOLAPA_EDGE_MARGIN_PX, right));
  }

  private clampBottom(bottom: number): number {
    const max = Math.max(
      MOBILE_SOLAPA_EDGE_MARGIN_PX,
      window.innerHeight - MOBILE_SOLAPA_SIZE_PX - MOBILE_SOLAPA_EDGE_MARGIN_PX
    );
    return Math.min(max, Math.max(MOBILE_SOLAPA_EDGE_MARGIN_PX, bottom));
  }
}
